/**
 * SiteLogoContext
 * Provides the site logo URL from admin-managed content (site_logo).
 * Used by Navbar, Footer, and auth pages (Login, Register, ForgotPassword).
 * Caches logo in sessionStorage (1h TTL) so we don't refetch on every load.
 */

import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { getContentImageUrl } from '../hooks/useSiteContent';

const SiteLogoContext = createContext({
  logoUrl: null,
  logoAlt: '',
  loading: false,
});

const FALLBACK_LOGO = '/logo.jpeg';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CACHE_KEY = 'luvia_site_logo';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function readLogoCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.url || !data?.cachedAt) return null;
    if (Date.now() - data.cachedAt > CACHE_TTL_MS) return null;
    return { url: data.url, alt: data.alt || 'Luvia' };
  } catch {
    return null;
  }
}

function writeLogoCache(url, alt) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      url,
      alt: alt || 'Luvia',
      cachedAt: Date.now(),
    }));
  } catch (_) {}
}

export function SiteLogoProvider({ children }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  const value = useMemo(() => {
    const hasImage = content?.image && (content.image.secure_url || content.image.url);
    const logoUrl = hasImage
      ? getContentImageUrl(content, FALLBACK_LOGO)
      : null;
    const logoAlt = content?.image?.alt || content?.title || 'Luvia';
    return {
      logoUrl,
      logoAlt,
      loading,
      resolvedLogoUrl: logoUrl || FALLBACK_LOGO,
    };
  }, [content, loading]);

  const fetchLogo = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    fetch(`${API_URL}/content/site_logo`, { signal: controller.signal, cache: 'default' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('fetch'))))
      .then((data) => {
        if (controller.signal.aborted) return;
        if (data?.success && data?.data) {
          const contentData = {
            ...data.data,
            updatedAt: data.data.updatedAt
              ? (typeof data.data.updatedAt === 'number'
                ? data.data.updatedAt
                : new Date(data.data.updatedAt).getTime())
              : Date.now(),
          };
          setContent(contentData);
          const hasImg = contentData?.image && (contentData.image.secure_url || contentData.image.url);
          const url = hasImg ? getContentImageUrl(contentData, FALLBACK_LOGO) : FALLBACK_LOGO;
          const alt = contentData?.image?.alt || contentData?.title || 'Luvia';
          writeLogoCache(url, alt);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setContent(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const cached = readLogoCache();
    if (cached) {
      setContent({ image: { url: cached.url }, title: cached.alt });
      setLoading(false);
      fetchLogo();
      return () => { if (abortRef.current) abortRef.current.abort(); };
    }
    setLoading(true);
    fetchLogo();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchLogo]);

  return (
    <SiteLogoContext.Provider value={value}>
      {children}
    </SiteLogoContext.Provider>
  );
}

export function useSiteLogo() {
  const context = useContext(SiteLogoContext);
  if (context === undefined) {
    throw new Error('useSiteLogo must be used within a SiteLogoProvider');
  }
  return context;
}
